import React from 'react';

import { Box } from '@chakra-ui/react';
import { useDateTimePicker } from 'components/hooks/useDatePicker';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { ONE_ON_ONE_STATUSES } from 'constants/ProjectConstants';
import {
  CancelMeetingIcon,
  ReopenMeetingIcon,
  RescheduleMeetingIcon,
  ShareMeetingIcon,
  StyledIcon,
} from 'constants/icons';
import theme from 'constants/theme';

import { showErrorSnackbar, showSuccessSnackbar } from 'services/snackbar';

import CircularOutlineIcon from 'uiToolkit/CircularOutlineIcon';
import { Typography4 } from 'uiToolkit/Typography';

import { StyledDotsIcon, StyledMenu, StyledMenuItem } from './Header';

export const MeetingMenu = ({
  onDateTimeChange,
  startDateTime,
  endDateTime,
  meetingStatus,
  handleReOpen,
  toggleCancelMeetingModal,
  isDisabled = false,
}) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const dispatch = useDispatch();

  const { pathname } = useLocation();
  const host = `${window.location.protocol}//${window.location.host}`;

  const openDateTimePicker = useDateTimePicker(onDateTimeChange);

  const handleClick = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = async (e) => {
    e.stopPropagation();
    setAnchorEl(null);
  };

  const onCopy = () => {
    navigator.clipboard.writeText(host + pathname);
    dispatch(showSuccessSnackbar('Successfully copied url to clipboard.'));
  };

  const getConditionalMenuOptions = (currentStatus) => {
    const optionsArray = [{ label: 'Share', value: 'SHARE', icon: <ShareMeetingIcon /> }];
    if (isDisabled) {
      return optionsArray;
    }

    if (currentStatus === ONE_ON_ONE_STATUSES.ACTIVE) {
      optionsArray.push(
        { label: 'Reschedule', value: 'RESCHEDULE', icon: <RescheduleMeetingIcon /> },
        { label: 'Cancel', value: 'CANCEL', icon: <CancelMeetingIcon /> }
      );
    }

    if (currentStatus === ONE_ON_ONE_STATUSES.ACKNOWLEDGED) {
      optionsArray.push(
        {
          label: 'Reopen',
          value: 'REOPEN',
          icon: <ReopenMeetingIcon />,
        },
        { label: 'Cancel', value: 'CANCEL', icon: <CancelMeetingIcon /> }
      );
    }
    if (currentStatus === ONE_ON_ONE_STATUSES.CANCELLED) {
      optionsArray.push({
        label: 'Reopen',
        value: 'REOPEN',
        icon: <ReopenMeetingIcon />,
      });
    }

    return optionsArray;
  };

  const generateUrl = () => {};

  const MENU_ACTIONS = getConditionalMenuOptions(meetingStatus);

  const onClick = async (e, option) => {
    try {
      if (option === 'SHARE') {
        onCopy();
        handleClose(e);
      }
      if (option === 'RESCHEDULE') {
        openDateTimePicker({ initialDate: startDateTime, endDate: endDateTime, openTo: 'hours' });
        handleClose(e);
      }
      if (option === 'CANCEL') {
        toggleCancelMeetingModal();
        handleClose(e);
      }
      if (option === 'REOPEN') {
        handleReOpen();
        handleClose(e);
      }
    } catch (err) {
      const message = err.message || `Error in ${String(option).toLowerCase()}ing`;
      console.error(message);
      dispatch(showErrorSnackbar(message));
    }
  };

  return (
    <Box ml="1rem">
      <CircularOutlineIcon onClick={handleClick} color="primary" icon={StyledDotsIcon} />
      <StyledMenu anchorEl={anchorEl} keepMounted open={open} onClose={handleClose}>
        {MENU_ACTIONS.map(({ label, value, icon }, index) => (
          <StyledMenuItem key={index} onClick={(e) => onClick(e, value)}>
            <StyledIcon size="tiny" rightGutter={1} color={theme.palette.grey.dark}>
              {icon}
            </StyledIcon>
            <Typography4 customColor={theme.palette.text.primary}>{label}</Typography4>
          </StyledMenuItem>
        ))}
      </StyledMenu>
    </Box>
  );
};
